import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { PointerLockControls } from "three/examples/jsm/Addons.js";
import GUI from "lil-gui";
import { FontLoader } from "three/addons/loaders/FontLoader.js";
import { TextGeometry } from "three/addons/geometries/TextGeometry.js";
import { Sky } from "three/examples/jsm/Addons.js";
import { EXRLoader } from "three/examples/jsm/Addons.js";

/**
 * Base
 */
// Debug
const gui = new GUI();
gui.hide();

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader();
const matCapTexture = textureLoader.load("textures/matcaps/10.png");
matCapTexture.colorSpace = THREE.SRGBColorSpace;

const donutMatCapTexture = textureLoader.load("textures/matcaps/8.png");
donutMatCapTexture.colorSpace = THREE.SRGBColorSpace;

const exrLoader = new EXRLoader();
exrLoader.load("./starmap_2020_4k.exr", (map) => {
	map.mapping = THREE.EquirectangularReflectionMapping;

	scene.environment = map;
	scene.background = map;
});

/**
 * Font Loader and Font Object
 */
const fontLoader = new FontLoader();
fontLoader.load("/fonts/helvetiker_regular.typeface.json", (font) => {
	const textGeometry = new TextGeometry("Prabh Binner", {
		font: font,
		size: 0.5,
		depth: 0.2,
		curveSegments: 5,
		bevelEnabled: true,
		bevelThickness: 0.03,
		bevelSize: 0.02,
		bevelOffset: 0,
		bevelSegments: 4,
	});
	const material = new THREE.MeshMatcapMaterial({
		matcap: matCapTexture,
	});
	const text = new THREE.Mesh(textGeometry, material);
	scene.add(text);

	// textGeometry.computeBoundingBox();
	// textGeometry.translate(
	// 	-textGeometry.boundingBox.max.x * 0.5,
	// 	-textGeometry.boundingBox.max.y * 0.5,
	// 	-textGeometry.boundingBox.max.z * 0.5,
	// );

	textGeometry.center();
	text.rotation.y = Math.PI;

	/**
	 * Bubbles
	 */
	// const bubbles = new THREE.Group();
	// scene.add(bubbles);

	// const bubbleGeo = new THREE.SphereGeometry(0.5, 64, 64);
	// const bubbleMat = new THREE.MeshPhysicalMaterial();
	// bubbleMat.metalness = 0;
	// bubbleMat.roughness = 0;
	// bubbleMat.clearcoat = 1;
	// bubbleMat.clearcoatRoughness = 0;
	// bubbleMat.sheen = 1;
	// bubbleMat.sheenRoughness = 0.5;
	// bubbleMat.sheenColor.set(1, 1, 1);
	// bubbleMat.iridescence = 1;
	// bubbleMat.transmission = 1;
	// bubbleMat.iridescenceIOR = 1;
	// bubbleMat.iridescenceThicknessRange = [100, 800];
	// bubbleMat.ior = 1.5;
	// bubbleMat.thickness = 0.5;

	// gui.add(bubbleMat, "clearcoat").min(0).max(1).step(0.0001);
	// gui.add(bubbleMat, "clearcoatRoughness").min(0).max(1).step(0.0001);
	// gui.add(bubbleMat, "sheen").min(0).max(1).step(0.0001);
	// gui.add(bubbleMat, "sheenRoughness").min(0).max(1).step(0.0001);
	// gui.addColor(bubbleMat, "sheenColor");
	// gui.add(bubbleMat, "iridescence").min(0).max(1).step(0.0001);
	// gui.add(bubbleMat, "iridescenceIOR").min(1).max(2.333).step(0.0001);
	// gui.add(bubbleMat.iridescenceThicknessRange, "0").min(1).max(1000).step(1);
	// gui.add(bubbleMat.iridescenceThicknessRange, "1").min(1).max(1000).step(1);
	// gui.add(bubbleMat, "transmission").min(0).max(1).step(0.0001);
	// gui.add(bubbleMat, "ior").min(1).max(10).step(0.0001);
	// gui.add(bubbleMat, "thickness").min(0).max(1).step(0.0001);

	// const bubble = new THREE.Mesh(bubbleGeo, bubbleMat);

	// for (let i = 0; i < 100; i++) {
	// 	const bubble = new THREE.Mesh(bubbleGeo, bubbleMat);

	// 	bubbles.add(bubble);
	// }

	/**
	 * Donuts
	 */
	const donuts = new THREE.Group();
	scene.add(donuts);

	const donutGeo = new THREE.TorusGeometry(0.3, 0.2, 20, 45);
	const donutMat = new THREE.MeshMatcapMaterial({
		matcap: donutMatCapTexture,
	});

	for (let i = 0; i < 1000; i++) {
		const donut = new THREE.Mesh(donutGeo, donutMat);

		donut.position.x = (Math.random() - 0.5) * 100;
		donut.position.y = (Math.random() - 0.5) * 100;
		donut.position.z = (Math.random() - 0.5) * 100;

		donut.rotation.x = Math.random() * Math.PI;
		donut.rotation.y = Math.random() * Math.PI;

		const scale = Math.random();
		donut.scale.setScalar(scale);

		donuts.add(donut);
	}
});

/**
 * Sizes
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener("resize", (e) => {
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * FullScreen for Browser plus iOS support
 */
window.addEventListener("dblclick", (event) => {
	const fullScreenSize =
		document.fullscreenElement || document.webkitFullscreenElement;

	if (!fullScreenSize) {
		if (canvas.requestFullscreen) {
			canvas.requestFullscreen();
		} else if (canvas.webkitFullscreenElement) {
			canvas.webkitFullscreenElement();
		}
	} else {
		if (document.exitFullscreen) {
			document.exitFullscreen();
		} else {
			document.webkitExitFullscreen();
		}
	}
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
	75,
	sizes.width / sizes.height,
	0.1,
	100,
);
camera.position.x = 0;
camera.position.y = 1;
camera.position.z = -3.5;
scene.add(camera);

// Controls
const orbitcontrols = new OrbitControls(camera, canvas);
orbitcontrols.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
	const elapsedTime = clock.getElapsedTime();

	// Update controls
	orbitcontrols.update();

	// Render
	renderer.render(scene, camera);

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
